import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, ExternalLink, CheckCircle, XCircle, HelpCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { getEvalRun, getRunComparison } from '@/services/evalApi';

interface JudgeResult {
  verdict: 'pass' | 'fail' | 'partial' | 'error';
  overall_score: number;
  task_completed?: boolean;
  strengths?: string[];
  issues?: string[];
}

interface DateAwarenessWarning {
  type: 'non_date_aware_tool_attempt' | 'unknown_tool_date_awareness';
  tool_name: string;
  timestamp: string;
  message: string;
  severity: 'warning' | 'info';
}

interface EvalFunctionResult {
  passed?: boolean;
  score?: number;
  success?: boolean;
  eval_name?: string;
  error?: string;
  details?: Record<string, unknown>;
  ground_truth?: unknown;
}

interface Trial {
  id: string;
  variant_id?: string;
  status: string;
  input_data: unknown;
  output_data?: { final_output?: string } | unknown;
  judge_result?: JudgeResult;
  eval_results?: Record<string, EvalFunctionResult>;
  metrics?: { latency_ms?: number };
  trajectory_id?: string;
  agent_version_used?: number;
  agent_content_hash?: string;
  judge_version_used?: number;
  date_awareness_warnings?: DateAwarenessWarning[];
}

interface RunData {
  id: string;
  status: string;
  total_trials: number;
  completed_trials: number;
  started_at?: string;
  completed_at?: string;
  trials?: Trial[];
}

// Enhanced comparison data types
interface MetricStats {
  min: number;
  max: number;
  avg: number;
  count: number;
}

interface CriterionStats {
  name: string;
  weight: number;
  avg_score: number;
  min_score: number;
  max_score: number;
  count: number;
}

interface InputScenarioStats {
  scenario_key: string;
  input_summary: string;
  trial_count: number;
  pass_rate: number;
  avg_score: number;
  avg_latency_ms?: number | null;
}

interface VariantConfig {
  model?: string;
  temperature?: number;
  tools?: string[];
  prompt?: string;
  [key: string]: unknown;
}

interface VariantComparisonData {
  trial_count: number;
  pass_count: number;
  fail_count: number;
  pass_rate: number;
  avg_score: number;
  avg_score_passes?: number | null;
  avg_score_fails?: number | null;
  min_score: number;
  max_score: number;
  avg_latency_ms?: number;
  metrics: Record<string, MetricStats>;
  criterion_scores: CriterionStats[];
  config: VariantConfig;
  by_input: InputScenarioStats[];
}

interface InputScenario {
  key: string;
  data: Record<string, unknown>;
  summary: string;
}

interface ComparisonData {
  run_id?: string;
  variants?: Record<string, VariantComparisonData>;
  config_diff_keys?: string[];
  input_scenarios?: InputScenario[];
}

function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    completed: 'default',
    completed_with_errors: 'secondary',
    running: 'secondary',
    pending: 'outline',
    failed: 'destructive',
    cancelled: 'outline',
  };
  const displayName = status === 'completed_with_errors' ? 'partial' : status;
  return <Badge variant={variants[status] || 'outline'}>{displayName}</Badge>;
}

function VerdictIcon({ verdict }: { verdict: string }) {
  if (verdict === 'pass') return <CheckCircle className="h-5 w-5 text-green-500" />;
  if (verdict === 'fail') return <XCircle className="h-5 w-5 text-red-500" />;
  if (verdict === 'error') return <AlertTriangle className="h-5 w-5 text-orange-500" />;
  return <HelpCircle className="h-5 w-5 text-yellow-500" />;
}

function TrialRow({ trial }: { trial: Trial }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-muted/50"
        onClick={() => setExpanded(!expanded)}
      >
        <TableCell>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-6 w-6">
              {expanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
            <code className="text-sm">{trial.id.slice(0, 8)}...</code>
          </div>
        </TableCell>
        <TableCell>
          <Badge variant="outline">{trial.variant_id || 'baseline'}</Badge>
        </TableCell>
        <TableCell>
          <StatusBadge status={trial.status} />
        </TableCell>
        <TableCell>
          {trial.judge_result ? (
            <div className="flex items-center gap-2">
              <VerdictIcon verdict={trial.judge_result.verdict} />
              <span className="text-sm">
                {(trial.judge_result.overall_score * 100).toFixed(0)}%
              </span>
            </div>
          ) : trial.eval_results ? (
            <div className="flex items-center gap-2">
              {(() => {
                // Aggregate results from all eval functions
                const results = Object.values(trial.eval_results);
                const passed = results.every((r) => r.passed);
                const score = results.length > 0
                  ? results.reduce((sum, r) => sum + (r.score ?? (r.passed ? 1 : 0)), 0) / results.length
                  : 0;
                return (
                  <>
                    <VerdictIcon verdict={passed ? 'pass' : 'fail'} />
                    <span className="text-sm">{(score * 100).toFixed(0)}%</span>
                  </>
                );
              })()}
            </div>
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell>
          {trial.metrics?.latency_ms
            ? `${(trial.metrics.latency_ms / 1000).toFixed(2)}s`
            : '-'}
        </TableCell>
        <TableCell>
          {trial.trajectory_id && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/trajectory/${trial.trajectory_id}`);
                  }}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>View Trajectory</TooltipContent>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={6} className="p-0">
            <div className="p-4 bg-muted/30">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-2">Input</h4>
                  <pre className="bg-muted p-2 rounded text-xs overflow-auto max-h-40">
                    {JSON.stringify(trial.input_data, null, 2)}
                  </pre>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-2">Output</h4>
                  <pre className="bg-muted p-2 rounded text-xs overflow-auto max-h-40">
                    {(() => {
                      if (!trial.output_data) return '-';
                      const output = trial.output_data as Record<string, unknown>;
                      // Check for final_output.text (common structure)
                      if (output.final_output && typeof output.final_output === 'object') {
                        const finalOutput = output.final_output as Record<string, unknown>;
                        if (typeof finalOutput.text === 'string') {
                          return finalOutput.text;
                        }
                      }
                      // Check for direct final_output string
                      if (typeof output.final_output === 'string') {
                        return output.final_output;
                      }
                      // Fallback to JSON
                      return JSON.stringify(trial.output_data, null, 2);
                    })()}
                  </pre>
                </div>
                {/* Version Info */}
                {(trial.agent_version_used || trial.judge_version_used) && (
                  <div className="col-span-full">
                    <h4 className="text-sm font-medium mb-2">Version Info</h4>
                    <div className="flex gap-4">
                      {trial.agent_version_used && (
                        <Badge variant="outline">
                          Agent v{trial.agent_version_used}
                          {trial.agent_content_hash && (
                            <span className="text-muted-foreground ml-1">
                              ({trial.agent_content_hash.slice(0, 8)})
                            </span>
                          )}
                        </Badge>
                      )}
                      {trial.judge_version_used && (
                        <Badge variant="outline">
                          Judge v{trial.judge_version_used}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
                {trial.judge_result && (
                  <div className="col-span-full">
                    <h4 className="text-sm font-medium mb-2">Judge Evaluation</h4>
                    <div className="border rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Verdict</p>
                          <div className="flex items-center gap-2">
                            <VerdictIcon verdict={trial.judge_result.verdict} />
                            <span className="font-bold uppercase">
                              {trial.judge_result.verdict}
                            </span>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Score</p>
                          <p className="font-bold">
                            {(trial.judge_result.overall_score * 100).toFixed(1)}%
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Task Completed</p>
                          <p>{trial.judge_result.task_completed ? 'Yes' : 'No'}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {trial.judge_result.strengths && trial.judge_result.strengths.length > 0 && (
                          <div>
                            <p className="text-sm text-muted-foreground mb-1">Strengths</p>
                            <ul className="list-disc list-inside text-sm space-y-1">
                              {trial.judge_result.strengths.map((s, i) => (
                                <li key={i}>{s}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {trial.judge_result.issues && trial.judge_result.issues.length > 0 && (
                          <div>
                            <p className="text-sm text-muted-foreground mb-1">Issues</p>
                            <ul className="list-disc list-inside text-sm space-y-1">
                              {trial.judge_result.issues.map((s, i) => (
                                <li key={i}>{s}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {!trial.judge_result && trial.eval_results && Object.keys(trial.eval_results).length > 0 && (
                  <div className="col-span-full">
                    <h4 className="text-sm font-medium mb-2">Eval Function Results</h4>
                    <div className="space-y-3">
                      {Object.entries(trial.eval_results).map(([funcName, result]) => (
                        <div key={funcName} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <VerdictIcon verdict={result.passed ? 'pass' : 'fail'} />
                              <span className="font-medium">{result.eval_name || funcName}</span>
                            </div>
                            {result.score !== undefined && (
                              <Badge variant="outline">
                                Score: {(result.score * 100).toFixed(0)}%
                              </Badge>
                            )}
                          </div>
                          {/* Show error if present */}
                          {result.error && (
                            <div className="mb-3 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
                              <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">Error</p>
                              <p className="text-sm text-red-700 dark:text-red-300">{result.error}</p>
                            </div>
                          )}
                          {result.details && Object.keys(result.details).length > 0 && (
                            <div className="mb-3">
                              <p className="text-sm text-muted-foreground mb-1">Details</p>
                              <div className="space-y-2 text-sm">
                                {Object.entries(result.details).map(([key, value]) => (
                                  <div key={key} className="bg-muted/50 rounded px-2 py-1">
                                    <span className="text-muted-foreground font-medium">{key}: </span>
                                    {key === 'output_preview' || (typeof value === 'string' && value.length > 100) ? (
                                      <pre className="mt-1 text-xs font-mono whitespace-pre-wrap overflow-auto max-h-40">
                                        {String(value)}
                                      </pre>
                                    ) : (
                                      <span className="font-mono">
                                        {typeof value === 'boolean' ? (value ? 'true' : 'false') :
                                         typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {result.ground_truth && (
                            <div>
                              <p className="text-sm text-muted-foreground mb-1">Ground Truth</p>
                              <pre className="bg-muted p-2 rounded text-xs overflow-auto max-h-32">
                                {JSON.stringify(result.ground_truth, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Date Awareness Warnings */}
                {trial.date_awareness_warnings && trial.date_awareness_warnings.length > 0 && (
                  <div className="col-span-full mt-4">
                    {/* Warning severity: known non-date-aware tools */}
                    {trial.date_awareness_warnings.filter(w => w.severity === 'warning').length > 0 && (
                      <Alert variant="destructive" className="mb-2">
                        <AlertTriangle className="h-4 w-4" />
                        <div className="ml-2">
                          <p className="font-medium">Non-Date-Aware Tools Used</p>
                          <AlertDescription>
                            <ul className="mt-1 list-disc list-inside text-sm">
                              {trial.date_awareness_warnings
                                .filter(w => w.severity === 'warning')
                                .map((w, i) => (
                                  <li key={i}><code>{w.tool_name}</code> - does not support date override</li>
                                ))}
                            </ul>
                          </AlertDescription>
                        </div>
                      </Alert>
                    )}
                    {/* Info severity: unknown tools (assumed safe) */}
                    {trial.date_awareness_warnings.filter(w => w.severity === 'info').length > 0 && (
                      <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
                        <HelpCircle className="h-4 w-4 text-blue-600" />
                        <div className="ml-2">
                          <p className="font-medium text-blue-800 dark:text-blue-200">Unknown Tool Date Support</p>
                          <AlertDescription className="text-blue-700 dark:text-blue-300 text-sm">
                            {trial.date_awareness_warnings.filter(w => w.severity === 'info').length} tool(s)
                            have no date override metadata (assumed safe)
                          </AlertDescription>
                        </div>
                      </Alert>
                    )}
                  </div>
                )}
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// Helper to format metric values for display
function formatMetricValue(metricName: string, value: number | undefined | null): string {
  if (value === undefined || value === null) return '-';
  if (metricName.includes('latency') || metricName.includes('duration')) {
    return `${(value / 1000).toFixed(2)}s`;
  }
  if (metricName.includes('rate') || metricName.includes('score')) {
    return `${(value * 100).toFixed(1)}%`;
  }
  if (Number.isInteger(value)) {
    return value.toString();
  }
  return value.toFixed(2);
}

// Helper to find best/worst value in a row
function findBestWorstForMetric(
  metricName: string,
  variantNames: string[],
  getValue: (variantName: string) => number | undefined
): { best: string | null; worst: string | null } {
  const values = variantNames
    .map((name) => ({ name, value: getValue(name) }))
    .filter((v) => v.value !== undefined && v.value !== null);

  if (values.length === 0) return { best: null, worst: null };

  // For latency metrics, lower is better. For scores/rates, higher is better.
  const lowerIsBetter = metricName.includes('latency') || metricName.includes('duration');

  const sorted = [...values].sort((a, b) => (a.value || 0) - (b.value || 0));
  return {
    best: lowerIsBetter ? sorted[0].name : sorted[sorted.length - 1].name,
    worst: lowerIsBetter ? sorted[sorted.length - 1].name : sorted[0].name,
  };
}

// Metrics comparison table
function MetricsTable({
  comparison,
  variantNames,
}: {
  comparison: ComparisonData;
  variantNames: string[];
}) {
  const variants = comparison.variants || {};

  // Collect all unique metric names across variants
  const allMetricNames = new Set<string>();
  Object.values(variants).forEach((v) => {
    Object.keys(v.metrics || {}).forEach((m) => allMetricNames.add(m));
  });
  const metricNames = Array.from(allMetricNames).sort();

  // Also include base stats as metrics
  const baseMetrics = [
    { key: 'pass_rate', label: 'Pass Rate' },
    { key: 'avg_score', label: 'Avg Score (All)' },
    { key: 'avg_score_passes', label: 'Avg Score (Passes)' },
    { key: 'avg_score_fails', label: 'Avg Score (Fails)' },
    { key: 'trial_count', label: 'Trial Count' },
    { key: 'pass_count', label: 'Pass Count' },
  ];

  if (metricNames.length === 0 && variantNames.length === 0) {
    return <p className="text-muted-foreground py-4">No metrics data available</p>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[200px]">Metric</TableHead>
          {variantNames.map((name) => (
            <TableHead key={name} className="text-center">
              {name}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {/* Base metrics */}
        {baseMetrics.map(({ key, label }) => {
          const { best, worst } = findBestWorstForMetric(key, variantNames, (v) => {
            const vd = variants[v];
            return vd ? (vd as Record<string, number>)[key] : undefined;
          });
          return (
            <TableRow key={key}>
              <TableCell className="font-medium">{label}</TableCell>
              {variantNames.map((name) => {
                const vd = variants[name];
                const value = vd ? (vd as Record<string, number>)[key] : undefined;
                const isBest = name === best && variantNames.length > 1;
                const isWorst = name === worst && variantNames.length > 1 && best !== worst;
                return (
                  <TableCell
                    key={name}
                    className={`text-center ${isBest ? 'bg-green-50 dark:bg-green-950' : ''} ${isWorst ? 'bg-red-50 dark:bg-red-950' : ''}`}
                  >
                    {formatMetricValue(key, value)}
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })}
        {/* Detailed metrics with min/max/avg */}
        {metricNames.map((metricName) => {
          const { best, worst } = findBestWorstForMetric(metricName, variantNames, (v) => {
            return variants[v]?.metrics?.[metricName]?.avg;
          });
          return (
            <TableRow key={metricName}>
              <TableCell className="font-medium">
                {metricName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </TableCell>
              {variantNames.map((name) => {
                const stats = variants[name]?.metrics?.[metricName];
                const isBest = name === best && variantNames.length > 1;
                const isWorst = name === worst && variantNames.length > 1 && best !== worst;
                return (
                  <TableCell
                    key={name}
                    className={`text-center ${isBest ? 'bg-green-50 dark:bg-green-950' : ''} ${isWorst ? 'bg-red-50 dark:bg-red-950' : ''}`}
                  >
                    {stats ? (
                      <Tooltip>
                        <TooltipTrigger>
                          <span className="font-medium">
                            {formatMetricValue(metricName, stats.avg)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="text-xs">
                            <div>Min: {formatMetricValue(metricName, stats.min)}</div>
                            <div>Max: {formatMetricValue(metricName, stats.max)}</div>
                            <div>Count: {stats.count}</div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// Judge criteria comparison table
function CriteriaTable({
  comparison,
  variantNames,
}: {
  comparison: ComparisonData;
  variantNames: string[];
}) {
  const variants = comparison.variants || {};

  // Collect all unique criterion names
  const allCriterionNames = new Set<string>();
  const criterionWeights: Record<string, number> = {};
  Object.values(variants).forEach((v) => {
    (v.criterion_scores || []).forEach((cs) => {
      allCriterionNames.add(cs.name);
      criterionWeights[cs.name] = cs.weight;
    });
  });
  const criterionNames = Array.from(allCriterionNames).sort();

  if (criterionNames.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No judge criteria configured for this evaluation.</p>
        <p className="text-sm mt-2">
          Criteria breakdown appears when an LLM judge with defined criteria is used.
        </p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[200px]">Criterion</TableHead>
          <TableHead className="w-[80px] text-center">Weight</TableHead>
          {variantNames.map((name) => (
            <TableHead key={name} className="text-center">
              {name}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {criterionNames.map((criterionName) => {
          const { best, worst } = findBestWorstForMetric(criterionName, variantNames, (v) => {
            const cs = variants[v]?.criterion_scores?.find((c) => c.name === criterionName);
            return cs?.avg_score;
          });
          return (
            <TableRow key={criterionName}>
              <TableCell className="font-medium">{criterionName}</TableCell>
              <TableCell className="text-center text-muted-foreground">
                {((criterionWeights[criterionName] || 1) * 100).toFixed(0)}%
              </TableCell>
              {variantNames.map((name) => {
                const cs = variants[name]?.criterion_scores?.find((c) => c.name === criterionName);
                const isBest = name === best && variantNames.length > 1;
                const isWorst = name === worst && variantNames.length > 1 && best !== worst;
                return (
                  <TableCell
                    key={name}
                    className={`text-center ${isBest ? 'bg-green-50 dark:bg-green-950' : ''} ${isWorst ? 'bg-red-50 dark:bg-red-950' : ''}`}
                  >
                    {cs ? (
                      <Tooltip>
                        <TooltipTrigger>
                          <span className="font-medium">{(cs.avg_score * 100).toFixed(1)}%</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="text-xs">
                            <div>Min: {(cs.min_score * 100).toFixed(1)}%</div>
                            <div>Max: {(cs.max_score * 100).toFixed(1)}%</div>
                            <div>Samples: {cs.count}</div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// Config diff table
function ConfigDiffTable({
  comparison,
  variantNames,
}: {
  comparison: ComparisonData;
  variantNames: string[];
}) {
  const variants = comparison.variants || {};
  const diffKeys = comparison.config_diff_keys || [];

  // Also show all config keys if no diff keys (single variant)
  const allConfigKeys = new Set<string>();
  Object.values(variants).forEach((v) => {
    Object.keys(v.config || {}).forEach((k) => allConfigKeys.add(k));
  });
  const keysToShow = diffKeys.length > 0 ? diffKeys : Array.from(allConfigKeys).sort();

  if (keysToShow.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No configuration differences between variants.</p>
        <p className="text-sm mt-2">All variants are using the same configuration.</p>
      </div>
    );
  }

  // Helper to format config value
  const formatConfigValue = (value: unknown): string => {
    if (value === undefined || value === null) return '(default)';
    if (Array.isArray(value)) return value.join(', ') || '(empty)';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[200px]">Config Key</TableHead>
          {variantNames.map((name) => (
            <TableHead key={name} className="text-center">
              {name}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {keysToShow.map((key) => (
          <TableRow key={key}>
            <TableCell className="font-medium">{key}</TableCell>
            {variantNames.map((name) => {
              const value = variants[name]?.config?.[key];
              return (
                <TableCell key={name} className="text-center">
                  <Tooltip>
                    <TooltipTrigger>
                      <span className="truncate max-w-[200px] inline-block">
                        {formatConfigValue(value)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-md">
                      <pre className="text-xs whitespace-pre-wrap">
                        {JSON.stringify(value, null, 2)}
                      </pre>
                    </TooltipContent>
                  </Tooltip>
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// Input breakdown table
function InputBreakdownTable({
  comparison,
  variantNames,
}: {
  comparison: ComparisonData;
  variantNames: string[];
}) {
  const variants = comparison.variants || {};
  const inputScenarios = comparison.input_scenarios || [];

  if (inputScenarios.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No input scenario data available.</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[250px]">Input Scenario</TableHead>
          {variantNames.map((name) => (
            <TableHead key={name} className="text-center" colSpan={2}>
              {name}
            </TableHead>
          ))}
        </TableRow>
        <TableRow>
          <TableHead></TableHead>
          {variantNames.map((name) => (
            <React.Fragment key={name}>
              <TableHead className="text-center text-xs">Pass Rate</TableHead>
              <TableHead className="text-center text-xs">Avg Score</TableHead>
            </React.Fragment>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {inputScenarios.map((scenario) => {
          // Find best pass rate across variants for this scenario
          const passRates = variantNames.map((name) => {
            const byInput = variants[name]?.by_input?.find(
              (bi) => bi.scenario_key === scenario.key
            );
            return { name, value: byInput?.pass_rate };
          });
          const bestPassRate = passRates
            .filter((p) => p.value !== undefined)
            .sort((a, b) => (b.value || 0) - (a.value || 0))[0]?.name;

          return (
            <TableRow key={scenario.key}>
              <TableCell>
                <Tooltip>
                  <TooltipTrigger>
                    <span className="text-sm truncate max-w-[230px] inline-block">
                      {scenario.summary}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-lg">
                    <pre className="text-xs whitespace-pre-wrap max-h-60 overflow-auto">
                      {JSON.stringify(scenario.data, null, 2)}
                    </pre>
                  </TooltipContent>
                </Tooltip>
              </TableCell>
              {variantNames.map((name) => {
                const byInput = variants[name]?.by_input?.find(
                  (bi) => bi.scenario_key === scenario.key
                );
                const isBest = name === bestPassRate && variantNames.length > 1;
                return (
                  <React.Fragment key={name}>
                    <TableCell
                      className={`text-center ${isBest ? 'bg-green-50 dark:bg-green-950' : ''}`}
                    >
                      {byInput ? `${(byInput.pass_rate * 100).toFixed(0)}%` : '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      {byInput ? `${(byInput.avg_score * 100).toFixed(0)}%` : '-'}
                    </TableCell>
                  </React.Fragment>
                );
              })}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// Main comparison view with tabs
function ComparisonView({ comparison }: { comparison: ComparisonData }) {
  const variantNames = Object.keys(comparison.variants || {});

  if (variantNames.length === 0) {
    return <p className="text-muted-foreground">No comparison data available</p>;
  }

  // Check what data is available
  const hasMetrics = Object.values(comparison.variants || {}).some(
    (v) => Object.keys(v.metrics || {}).length > 0
  );
  const hasCriteria = Object.values(comparison.variants || {}).some(
    (v) => (v.criterion_scores || []).length > 0
  );
  const hasInputs = (comparison.input_scenarios || []).length > 0;
  const hasConfig =
    (comparison.config_diff_keys || []).length > 0 ||
    Object.values(comparison.variants || {}).some((v) => Object.keys(v.config || {}).length > 0);

  return (
    <Tabs defaultValue="metrics">
      <TabsList className="mb-4">
        <TabsTrigger value="metrics">Metrics</TabsTrigger>
        <TabsTrigger value="criteria" disabled={!hasCriteria}>
          Judge Criteria
        </TabsTrigger>
        <TabsTrigger value="config" disabled={!hasConfig}>
          Config Diff
        </TabsTrigger>
        <TabsTrigger value="inputs" disabled={!hasInputs}>
          By Input
        </TabsTrigger>
      </TabsList>

      <TabsContent value="metrics" className="mt-0">
        <MetricsTable comparison={comparison} variantNames={variantNames} />
      </TabsContent>

      <TabsContent value="criteria" className="mt-0">
        <CriteriaTable comparison={comparison} variantNames={variantNames} />
      </TabsContent>

      <TabsContent value="config" className="mt-0">
        <ConfigDiffTable comparison={comparison} variantNames={variantNames} />
      </TabsContent>

      <TabsContent value="inputs" className="mt-0">
        <InputBreakdownTable comparison={comparison} variantNames={variantNames} />
      </TabsContent>
    </Tabs>
  );
}

export function EvalRunDetail() {
  const { evalId, runId } = useParams<{ evalId: string; runId: string }>();
  const navigate = useNavigate();
  const [runData, setRunData] = useState<RunData | null>(null);
  const [comparison, setComparison] = useState<ComparisonData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch data function for initial load and polling
  const fetchData = async (isPolling = false) => {
    if (!runId) return;
    try {
      if (!isPolling) setLoading(true);
      const [run, comp] = await Promise.all([
        getEvalRun(runId),
        getRunComparison(runId),
      ]);
      setRunData(run);
      setComparison(comp);
      setError(null);
    } catch (err) {
      if (!isPolling) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      }
    } finally {
      if (!isPolling) setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [runId]);

  // Smart polling: poll when running, stop when complete or after 10 minutes
  useEffect(() => {
    const isRunning = runData?.status === 'running';
    if (!isRunning) return;

    const startTime = Date.now();
    const maxPollingDuration = 10 * 60 * 1000; // 10 minutes

    // Poll every 15 seconds while running
    const interval = setInterval(() => {
      // Stop polling after 10 minutes to avoid stale connections
      if (Date.now() - startTime > maxPollingDuration) {
        clearInterval(interval);
        return;
      }
      fetchData(true);
    }, 15000);

    return () => clearInterval(interval);
  }, [runId, runData?.status]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!runData) {
    return (
      <div className="container max-w-6xl py-8">
        <Alert variant="destructive">
          <AlertDescription>Run not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  const passCount = runData.trials?.filter((t) => {
    // Check judge_result first
    if (t.judge_result?.verdict === 'pass') return true;
    // Fallback to eval_results
    if (t.eval_results) {
      return Object.values(t.eval_results).every((r) => r.passed);
    }
    return false;
  }).length || 0;

  return (
    <div className="container max-w-6xl py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/evals/${evalId}`)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold">Evaluation Run</h1>
          <code className="text-sm text-muted-foreground">{runData.id}</code>
        </div>
        <StatusBadge status={runData.status} />
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Trials</p>
            <p className="text-2xl font-bold">{runData.total_trials}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Completed</p>
            <p className="text-2xl font-bold text-green-600">{runData.completed_trials}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Pass Rate</p>
            <p className="text-2xl font-bold">
              {runData.trials?.length
                ? ((passCount / runData.trials.length) * 100).toFixed(0)
                : 0}
              %
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Duration</p>
            <p className="text-2xl font-bold">
              {runData.started_at && runData.completed_at
                ? `${((new Date(runData.completed_at).getTime() - new Date(runData.started_at).getTime()) / 1000).toFixed(1)}s`
                : '-'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="comparison" className="border rounded-lg">
        <TabsList className="w-full justify-start border-b rounded-none bg-transparent h-auto p-0">
          <TabsTrigger
            value="comparison"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
          >
            A/B Comparison
          </TabsTrigger>
          <TabsTrigger
            value="trials"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
          >
            Trials ({runData.trials?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="comparison" className="p-4">
          {comparison ? (
            <ComparisonView comparison={comparison} />
          ) : (
            <p className="text-muted-foreground">Loading comparison data...</p>
          )}
        </TabsContent>

        <TabsContent value="trials" className="p-0 m-0">
          <Table className="table-fixed w-full">
            <colgroup>
              <col className="w-[140px]" />
              <col className="w-[100px]" />
              <col className="w-[110px]" />
              <col className="w-[80px]" />
              <col className="w-[90px]" />
              <col className="w-[80px]" />
            </colgroup>
            <TableHeader>
              <TableRow>
                <TableHead>Trial</TableHead>
                <TableHead>Variant</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Score</TableHead>
                <TableHead>Latency</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runData.trials?.map((trial) => (
                <TrialRow key={trial.id} trial={trial} />
              ))}
            </TableBody>
          </Table>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default EvalRunDetail;
